import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

interface TableParams {
  page: number;
  pageSize: number;
}

const DEFAULTS: TableParams = { page: 0, pageSize: 20 };

/**
 * Syncs table pagination state with URL search params.
 *
 * Reading: derives page/pageSize from ?page=&pageSize= in the URL.
 * Writing: updates the URL without navigation (replace mode).
 *
 * @param defaults Optional overrides for default page/pageSize.
 */
export function useTableSearchParams(defaults?: Partial<TableParams>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const merged = { ...DEFAULTS, ...defaults };

  const page = useMemo(() => {
    const raw = searchParams.get('page');
    if (raw === null) return merged.page;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) || parsed < 0 ? merged.page : parsed;
  }, [searchParams, merged.page]);

  const pageSize = useMemo(() => {
    const raw = searchParams.get('pageSize');
    if (raw === null) return merged.pageSize;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) || parsed < 1 ? merged.pageSize : parsed;
  }, [searchParams, merged.pageSize]);

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('page', String(newPage));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setPageSize = useCallback(
    (newSize: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('pageSize', String(newSize));
          next.set('page', '0'); // reset to first page on size change
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const getParam = useCallback(
    (key: string): string | undefined => searchParams.get(key) ?? undefined,
    [searchParams]
  );

  const setParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          next.set('page', '0');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSelected = useCallback(
    (testId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('selected', testId);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return { page, pageSize, setPage, setPageSize, getParam, setParams, setSelected };
}
